import { useState } from "react";
import { t } from "../i18n";
import { WEEKLY_PLAN_PRODUCT_ID, MONTHLY_PLAN_PRODUCT_ID } from "@lib/planLimits";
import type { PlanInfo } from "../domain/types";
import { Bell, Check, Eye, Lock, RotateCcw, ShieldCheck } from "lucide-react";

interface PaywallCardProps {
  targetHandle?: string;
  onSelectPlan: (plan: PlanInfo) => void;
  onRestorePurchase?: () => void;
  isLoading: boolean;
  isRestoring?: boolean;
  error?: string;
}

const plans: PlanInfo[] = [
  {
    productId: WEEKLY_PLAN_PRODUCT_ID,
    title: "Weekly",
    period: "weekly",
    monitorLimit: 3,
  },
  {
    productId: MONTHLY_PLAN_PRODUCT_ID,
    title: "Monthly",
    period: "monthly",
    monitorLimit: 3,
  },
];

export function PaywallCard({
  targetHandle,
  onSelectPlan,
  onRestorePurchase,
  isLoading,
  isRestoring = false,
  error,
}: PaywallCardProps) {
  const [selectedPlan, setSelectedPlan] = useState<PlanInfo>(plans[1]);
  const isBusy = isLoading || isRestoring;

  return (
    <div className="app-paywall-card">
      <div className="app-paywall-head">
        <span className="app-settings-icon"><Lock size={18} /></span>
        <div>
          <h2>
            {targetHandle
              ? t("billingRenewTitleTarget", { handle: targetHandle })
              : t("billingRenewTitle")}
          </h2>
          <p>
            {targetHandle
              ? t("billingRenewBodyTarget", { handle: targetHandle })
              : t("billingRenewBody")}
          </p>
        </div>
      </div>

      {error && (
        <div className="app-settings-error">
          {error}
        </div>
      )}

      <div className="app-paywall-benefits" aria-label={t("billingIncluded")}>
        <span><Eye size={16} /> {t("billingFeatureAnonymous")}</span>
        <span><Bell size={16} /> {t("billingFeatureAlerts")}</span>
        <span><ShieldCheck size={16} /> {t("billingFeatureDirect")}</span>
      </div>

      <div className="app-plan-list">
        {plans.map((plan) => (
          <button
            key={plan.productId}
            type="button"
            onClick={() => setSelectedPlan(plan)}
            disabled={isBusy}
            className={`app-plan-row ${selectedPlan.productId === plan.productId ? "is-selected" : ""}`}
            aria-pressed={selectedPlan.productId === plan.productId}
          >
            <div>
              <div className="app-plan-title">
                <span>{t(plan.period === "weekly" ? "paywallPlanWeekly" : "paywallPlanMonthly")}</span>
                {plan.period === "monthly" && (
                  <em>
                    {t("paywallBestValue")}
                  </em>
                )}
              </div>
              <p>
                {plan.period === "weekly"
                  ? t("billingPlanWeeklyMeta", { count: plan.monitorLimit })
                  : t("billingPlanMonthlyMeta", { count: plan.monitorLimit })}
              </p>
            </div>
            <span className="app-plan-check">
              {selectedPlan.productId === plan.productId && <Check size={16} />}
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onSelectPlan(selectedPlan)}
        disabled={isBusy}
        className="app-primary-button app-paywall-cta"
      >
        <Eye size={18} />
        {isLoading
          ? t("paywallActivatingPlan")
          : targetHandle
            ? t("billingStartTarget", { handle: targetHandle })
            : t("renewalCta")}
      </button>

      <p className="app-paywall-fineprint">
        {t("paywallTerms")}
      </p>
      <p className="app-paywall-fineprint">
        {t("paywallBillingNote")}
      </p>

      <button
        type="button"
        onClick={onRestorePurchase}
        disabled={isBusy || !onRestorePurchase}
        className="app-restore-button"
      >
        <RotateCcw size={16} />
        {isRestoring ? t("paywallRestoring") : t("paywallRestore")}
      </button>
    </div>
  );
}
